import { Flex, Text } from '@chakra-ui/react'
import IconLink from '~/assets/svgs/icon-link.svg?react'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DateTimeRangeDropdown } from '~/components/shared/DateTimeRangeDropdown'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { SearchInput } from '~/components/shared/SearchInput'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useClipboard } from '~/hooks/useClipboard'
import { CallsFilterButton } from '~/pages/Calls/CallsFilterButton'
import { textStyleKeys } from '~/themes/textStyles'
import { DateRange, SearchValue, SelectedFilterData } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

import { useCallsViewModelContext } from './Calls.viewmodel'

interface CallsListViewHeaderProps {
  searchValue?: SearchValue
  dateTimeRange?: DateRange
  callCount?: number
  tableSize?: Size
  onActiveFiltersChanged?: (activeFilters: SelectedFilterData[]) => void
  onDateTimeRangeChanged?: (dateTimeRange: DateRange) => void
  onSearchValueChanged?: (searchValue: SearchValue) => void
}

export const CallsListViewHeader = ({
  searchValue,
  dateTimeRange,
  tableSize,
  onActiveFiltersChanged,
  onDateTimeRangeChanged,
  onSearchValueChanged,
}: CallsListViewHeaderProps) => {
  const { toastSuccess, toastError } = useToastAlert()
  const { state } = useCallsViewModelContext()
  const { copyToClipboard } = useClipboard({
    onSuccess: () => toastSuccess('Link copied to clipboard'),
    onFailure: () => toastError('Failed to copy link to clipboard.'),
  })

  const handleCopyLink = () => {
    try {
      const url = location.href
      copyToClipboard(url)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Flex
      minHeight="4.5rem"
      height="4.5rem"
      px="1.25rem"
      alignItems="center"
      gap="0.75rem"
      alignSelf="stretch"
      borderBottom="2px solid"
      bg="gray.200"
      borderColor="gray.400"
    >
      <Flex
        data-testid="input-wrapper"
        alignItems="center"
        gap="0.75rem"
        flex="1"
      >
        <SearchInput
          value={searchValue?.label ?? ''}
          placeholder="Search a name or number"
          maxWidth="25rem"
          gap="0.5rem"
          flex="1 0 0"
          onChange={onSearchValueChanged}
        />
        <DateTimeRangeDropdown
          onValueChange={onDateTimeRangeChanged}
          useQuickActionButton={tableSize === 'sm'}
          dateTimeRange={dateTimeRange}
        />
        <CallsFilterButton
          onActiveFiltersChanged={onActiveFiltersChanged}
          useQuickActionButton={tableSize === 'sm'}
        />
        <Text textStyle={textStyleKeys.Body2}>
          {state?.totalFilteredCallCount ?? 0} Calls
        </Text>
      </Flex>
      <Flex alignItems="flex-end">
        <QuickActionButton
          variant={QuickActionButtonVariants.Default_200}
          tooltipText="Copy Link to Search"
          actionIcon={IconLink}
          onClick={() => handleCopyLink()}
        />
      </Flex>
    </Flex>
  )
}
